import React, { useState } from 'react';
import { MapPin, Phone, Clock, Wifi, Car, Users, Heart, Bike, Wine, UtensilsCrossed, Calendar, X } from 'lucide-react';
import { FadeInUp, FadeInLeft, FadeInRight, ScaleIn } from './ScrollAnimationWrapper';

const About = ({ openBooking, activeSection = 'hotel', language = 'de' }) => {
  const [showHoursPopup, setShowHoursPopup] = useState(false);
  
  const translations = {
    de: {
      mainTitle: 'Willkommen in Hattingen',
      mainSubtitle: 'Wo deutsche Gastfreundschaft auf italienische Leidenschaft trifft',
      hotel: {
        title: 'Willkommen bei La Casa',
        description: 'Erleben Sie erstklassigen Komfort in unserem traditionellen Hotel in ruhiger Lage.',
      },
      restaurant: {
        title: 'Willkommen bei La Casa',
        description: 'Genießen Sie traditionelle italienische Küche in elegantem Ambiente.',
      },
      features: {
        wifi: 'Kostenloses WLAN',
        parking: 'Kostenlose Parkplätze',
        family: 'Familienfreundlich',
        cycling: 'Fahrradrouten',
        wine: 'Exquisite Weine',
        authentic: 'Authentische Küche',
        events: 'Private Veranstaltungen',
        special: 'Romantische Atmosphäre'
      },
      location: {
        title: 'Unser Standort',
        phone: 'Telefon',
        address: 'Adresse',
        hours: 'Öffnungszeiten',
        available: 'Verfügbar',
        street: 'Wodantal 41',
        city: '45529 Hattingen, Deutschland'
      },
      hoursPopup: {
        title: 'Öffnungszeiten',
        restaurant: 'Restaurant',
        hotel: 'Hotel (Check-in)',
        restaurantHours: 'Freitag – Dienstag: 17:00 – 22:00',
        restaurantKitchen: '(Küche nimmt Bestellungen bis 21:00 an)',
        restaurantClosed: 'Mittwoch und Donnerstag: Geschlossen (Ruhetag)',
        hotelCheckin: 'Freitag – Dienstag: 15:00 – 18:00',
        hotelSpecial: 'Mittwoch und Donnerstag: 17:00 – 18:00',
        hotelPhone: '(Für Ankunft außerhalb dieser Zeit, bitte kontaktieren Sie uns telefonisch)'
      },
      cta: {
        title: 'Bereit für Ihren perfekten Aufenthalt?',
        subtitle: 'Erleben Sie Luxus und Komfort im Hotel & Restaurant La Casa im Herzen von Hattingen',
        button: 'Jetzt Buchen'
      }
    },
    en: {
      mainTitle: 'Welcome to Hattingen',
      mainSubtitle: 'Where German hospitality meets Italian passion',
      hotel: {
        title: 'Welcome to La Casa',
        description: 'Experience first-class comfort in our traditional hotel in a quiet location.',
      },
      restaurant: {
        title: 'Welcome to La Casa',
        description: 'Enjoy traditional Italian cuisine in elegant ambiance.',
      },
      features: {
        wifi: 'Free WiFi',
        parking: 'Free Parking',
        family: 'Family Friendly',
        cycling: 'Cycling Routes',
        wine: 'Exquisite Wines',
        authentic: 'Authentic Cuisine',
        events: 'Private Events',
        special: 'Romantic Atmosphere'
      },
      location: {
        title: 'Our Location',
        phone: 'Phone',
        address: 'Address',
        hours: 'Opening Hours',
        available: 'Available',
        street: 'Wodantal 41',
        city: '45529 Hattingen, Germany'
      },
      hoursPopup: {
        title: 'Opening Hours',
        restaurant: 'Restaurant',
        hotel: 'Hotel (Check-in)',
        restaurantHours: 'Friday – Tuesday: 5:00 PM – 10:00 PM',
        restaurantKitchen: '(Kitchen accepts orders until 9:00 PM)',
        restaurantClosed: 'Wednesday and Thursday: Closed',
        hotelCheckin: 'Friday – Tuesday: 3:00 PM – 6:00 PM',
        hotelSpecial: 'Wednesday and Thursday: 5:00 PM – 6:00 PM',
        hotelPhone: '(For arrival outside these hours, please contact us by phone)'
      },
      cta: {
        title: 'Ready for Your Perfect Stay?',
        subtitle: 'Experience luxury and comfort at Hotel & Restaurant La Casa in the heart of Hattingen',
        button: 'Book Now'
      }
    }
  };

  const t = translations[language];

  const hotelFeatures = [
    { icon: Wifi, text: t.features.wifi },
    { icon: Car, text: t.features.parking },
    { icon: Users, text: t.features.family },
    { icon: Bike, text: t.features.cycling },
  ];

  const restaurantFeatures = [
    { icon: Wine, text: t.features.wine },
    { icon: UtensilsCrossed, text: t.features.authentic },
    { icon: Calendar, text: t.features.events },
    { icon: Heart, text: t.features.special },
  ];

  return (
    <section id="about" className="section-padding bg-gradient-to-b from-luxury-cream via-luxury-pearl/60 to-luxury-cream">
      <div className="container-custom">
        
        {/* Main Header */}
        <FadeInUp threshold={0.1} className="text-center mb-20">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-4 text-luxury-charcoal">
            {t.mainTitle}
          </h1>
          <div className="w-32 h-1 bg-luxury-gold mx-auto mb-6" />
          <p className="text-xl md:text-2xl text-luxury-slate max-w-4xl mx-auto leading-relaxed font-serif">
            {t.mainSubtitle}
          </p>
        </FadeInUp>



        {/* Hotel Section */}
        {activeSection === 'hotel' && (
          <FadeInLeft className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <FadeInUp delay={100}>
                <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 text-luxury-charcoal text-center md:text-left">
                  {t.hotel.title}
                </h2>
                <div className="w-20 h-1 bg-luxury-gold mb-6 mx-auto md:mx-0" />
                <p className="text-lg text-luxury-slate leading-relaxed mb-6 font-serif text-center md:text-left">
                  {t.hotel.description}
                </p>
                
                {/* Hotel Features */}
                <FadeInUp delay={300} className="grid md:grid-cols-2 gap-4">
                  {hotelFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 bg-white/60 rounded-2xl glassmorphism hover:bg-white/80 transition-all duration-300 hover:scale-105"
                    >
                      <feature.icon className="text-luxury-gold flex-shrink-0" size={24} />
                      <span className="font-semibold text-luxury-charcoal ml-3">{feature.text}</span>
                    </div>
                  ))}
                </FadeInUp>
              </FadeInUp>
              
              <FadeInRight delay={200} className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="/hotelSlik1.jpg"
                  alt="Hotel Exterior"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-luxury-charcoal/60 via-transparent to-transparent" />
              </FadeInRight>
            </div>
          </FadeInLeft>
        )}

        {/* Location Section */}
        <ScaleIn delay={400} className="mb-20">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-luxury-charcoal via-black to-luxury-charcoal shadow-xl border-2 border-luxury-gold">
            
            {/* Decorative Background */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-luxury-gold rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-500 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 p-4 md:p-6">
              
              {/* Title */}
              <FadeInUp className="text-center mb-6">
                <div className="inline-flex items-center justify-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-luxury-gold rounded-full flex items-center justify-center">
                    <MapPin className="text-black" size={18} strokeWidth={2.5} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-display font-black text-white uppercase tracking-wider">
                    {t.location.title}
                  </h2>
                </div>
                <div className="w-24 h-0.5 bg-luxury-gold mx-auto" />
              </FadeInUp>

              {/* Contact Grid */}
              <FadeInUp delay={200} className="grid md:grid-cols-3 gap-3 md:gap-4 max-w-5xl mx-auto">
                
                {/* Phone */}
                <div className="group bg-luxury-gold/10 backdrop-blur-sm rounded-xl p-4 border-l-2 border-luxury-gold hover:bg-luxury-gold/20 transition-all duration-300 hover:scale-105">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-10 h-10 bg-luxury-gold rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Phone className="text-black" size={18} strokeWidth={2.5} />
                    </div>
                    <h3 className="text-sm font-display font-bold mb-1 text-luxury-gold uppercase tracking-wide">
                      {t.location.phone}
                    </h3>
                    <p className="text-white font-semibold text-sm">
                      <a href="tel:+49232456650" className="hover:text-luxury-gold transition-colors">
                        +49232456650
                      </a>
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div className="group bg-luxury-gold/10 backdrop-blur-sm rounded-xl p-4 border-l-2 border-luxury-gold hover:bg-luxury-gold/20 transition-all duration-300 hover:scale-105">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-10 h-10 bg-luxury-gold rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <MapPin className="text-black" size={18} strokeWidth={2.5} />
                    </div>
                    <h3 className="text-sm font-display font-bold mb-1 text-luxury-gold uppercase tracking-wide">
                      {t.location.address}
                    </h3>
                    <p className="text-white font-semibold text-sm leading-relaxed">
                      {t.location.street}<br />
                      {t.location.city}
                    </p>
                  </div>
                </div>

                {/* Hours - Clickable */}
                <button
                  onClick={() => setShowHoursPopup(true)}
                  className="group bg-luxury-gold/10 backdrop-blur-sm rounded-xl p-4 border-l-2 border-luxury-gold hover:bg-luxury-gold/20 transition-all duration-300 hover:scale-105 cursor-pointer w-full"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-10 h-10 bg-luxury-gold rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Clock className="text-black" size={18} strokeWidth={2.5} />
                    </div>
                    <h3 className="text-sm font-display font-bold mb-1 text-luxury-gold uppercase tracking-wide">
                      {t.location.hours}
                    </h3>
                    <p className="text-white font-semibold text-xs">
                      {language === 'de' ? 'Klicken für Details' : 'Click for details'}
                    </p>
                  </div>
                </button>

              </FadeInUp>
            </div>
          </div>
        </ScaleIn>

        {/* Restaurant Section */}
        {activeSection === 'restaurant' && (
          <FadeInRight className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              
              {/* Restaurant Image */}
              <FadeInLeft delay={200} className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/galerijaRestoran1.jpeg"
                  alt="Restaurant Interior"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-luxury-charcoal/60 via-transparent to-transparent" />
              </FadeInLeft>

              {/* Restaurant Text */}
              <FadeInUp delay={100}>
                <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 text-luxury-charcoal text-center md:text-left">
                  {t.restaurant.title}
                </h2>
                <div className="w-20 h-1 bg-luxury-gold mb-6 mx-auto md:mx-0" />
                <p className="text-lg text-luxury-slate leading-relaxed mb-6 font-serif text-center md:text-left">
                  {t.restaurant.description}
                </p>

                {/* Restaurant Features */}
                <FadeInUp delay={300} className="grid md:grid-cols-2 gap-4">
                  {restaurantFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 bg-white/60 rounded-2xl glassmorphism hover:bg-white/80 transition-all duration-300 hover:scale-105"
                    >
                      <feature.icon className="text-luxury-gold flex-shrink-0" size={24} />
                      <span className="font-semibold text-luxury-charcoal ml-3">{feature.text}</span>
                    </div>
                  ))}
                </FadeInUp>
              </FadeInUp>
            </div>
          </FadeInRight>
        )}



      </div>

      {/* Hours Popup */}
      {showHoursPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-[fadeIn_0.3s_ease-in-out]">
            {/* Close Button */}
            <button
              onClick={() => setShowHoursPopup(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>

            {/* Title */}
            <h3 className="text-2xl font-bold text-luxury-charcoal mb-6 pr-8">
              {t.hoursPopup.title}
            </h3>

            {/* Restaurant Hours */}
            <div className="mb-6 p-4 bg-luxury-gold/10 rounded-xl border-l-4 border-luxury-gold">
              <h4 className="text-lg font-bold text-luxury-charcoal mb-3 flex items-center">
                <UtensilsCrossed size={20} className="mr-2 text-luxury-gold" />
                {t.hoursPopup.restaurant}
              </h4>
              <div className="space-y-2 text-luxury-slate">
                <p className="font-semibold">{t.hoursPopup.restaurantHours}</p>
                <p className="text-sm">{t.hoursPopup.restaurantKitchen}</p>
                <p className="text-sm font-semibold text-red-600">{t.hoursPopup.restaurantClosed}</p>
              </div>
            </div>

            {/* Hotel Hours */}
            <div className="p-4 bg-luxury-pearl/30 rounded-xl border-l-4 border-luxury-charcoal">
              <h4 className="text-lg font-bold text-luxury-charcoal mb-3 flex items-center">
                <MapPin size={20} className="mr-2 text-luxury-charcoal" />
                {t.hoursPopup.hotel}
              </h4>
              <div className="space-y-2 text-luxury-slate">
                <p className="font-semibold">{t.hoursPopup.hotelCheckin}</p>
                <p className="font-semibold">{t.hoursPopup.hotelSpecial}</p>
                <p className="text-sm italic">{t.hoursPopup.hotelPhone}</p>
              </div>
            </div>

            {/* Close Button at Bottom */}
            <button
              onClick={() => setShowHoursPopup(false)}
              className="w-full mt-6 bg-luxury-gold hover:bg-luxury-darkGold text-white py-3 rounded-xl font-bold transition-all"
            >
              {language === 'de' ? 'Schließen' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default About;
